/*
 * Convert to Infographic — state machine driving a 5-tier fallback ladder:
 *   1. Region selected (table/text shape) -> generate at the region's size,
 *      merge into a clone of the active slide, insert right after the original.
 *   2. Region generate/merge fails -> regenerate at full-slide size, retry the
 *      merge with that content ("constrain" tier).
 *   3. Constrain also fails, or there was never a region (nothing selected, or
 *      a chart/picture with no readable text) -> plain full-slide infographic
 *      as a new slide; the original slide is untouched in every tier.
 *   4. A successful merge whose PowerPoint insert call itself fails -> abandon
 *      the merge, fall back to a standalone full-slide insert.
 *   5. Even that standalone insert rejected (insertSlidesFromBase64's strict
 *      OOXML schema check catches things the backend's well-formedness check
 *      doesn't) -> rebuild every shape from scratch via PptxGenJS's native API
 *      (always schema-valid; see infographicNativeBuilder.ts) before giving up.
 * `state` exposes two phases: "analyzing" (extraction + the AI request, which
 * covers both backend planner/composer stages in one round trip) and
 * "composing" (merging/wrapping and writing the result back into the deck).
 */

import { useCallback, useEffect, useRef, useState, type MutableRefObject } from "react";
import { useDeckorateHttp } from "../../../hooks/useDeckorateHttp";
import {
  describeInfographicFailure,
  getInfographicErrorMessage,
  INFOGRAPHIC_REQUEST_TIMEOUT_MS,
  isInfographicAbortError,
  logInfographicError,
  postConvertInfographic,
  type InfographicContent,
} from "../services/infographicApi";
import {
  extractInfographicContent,
  readDeckThemeColors,
  readSlideDimensionsEmu,
  type InfographicRegionTarget,
} from "../services/infographicContent";
import {
  buildInfographicSlideBase64,
  insertInfographicResult,
  NO_INFOGRAPHIC_XML_MESSAGE,
} from "../services/infographicInsert";
import { mergeInfographicIntoRegion } from "../services/infographicRegionMerge";
import { buildNativeInfographicSlide } from "../services/infographicNativeBuilder";
import type { SlideDimensions, ThemeColors } from "../../create-slide/service/slideGenerationApi";

export type InfographicPhase = "analyzing" | "composing";

export type ConvertInfographicState =
  | { status: "idle" }
  | { status: "running"; phase: InfographicPhase }
  | { status: "done"; infographicType: string | null; replacedSelection: boolean }
  /** The user stopped the run. Not a failure, so it is not styled as one. */
  | { status: "cancelled" }
  | { status: "error"; message: string };

const INFOGRAPHIC_FAILED_MESSAGE = "Creating the infographic failed";

interface AbortState {
  /** The controller for whichever convert_infographic call is currently in
   *  flight — calls are always sequential, never concurrent, so one slot
   *  suffices. `cancel()` aborts whatever this currently points at. */
  readonly abortRef: MutableRefObject<AbortController | null>;
  /** Distinguishes "our deadline elapsed" from "the user walked away", since
   *  both surface as an AbortError but deserve different copy. */
  readonly timedOutRef: MutableRefObject<boolean>;
}

/** Issues one HTTP call under its own deadline, so a later fallback tier's
 *  attempt gets a fresh timeout budget rather than sharing one across the
 *  whole (possibly multi-call) run. */
async function withRequestTimeout<T>(abortState: AbortState, fn: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const controller = new AbortController();
  abortState.abortRef.current = controller;
  const timeoutId = setTimeout(() => {
    abortState.timedOutRef.current = true;
    controller.abort();
  }, INFOGRAPHIC_REQUEST_TIMEOUT_MS);
  try {
    return await fn(controller.signal);
  } finally {
    clearTimeout(timeoutId);
    if (abortState.abortRef.current === controller) abortState.abortRef.current = null;
  }
}

interface GenerateArgs {
  readonly content: InfographicContent;
  readonly dims: SlideDimensions;
  readonly mode: "full_slide" | "region";
  readonly themeColors: ThemeColors | null;
  readonly userDescription: string;
}

/** One POST, validated — throws with the API's own error text (or the
 *  generic "no usable layout" message) when the backend didn't produce XML. */
async function generateInfographicXml(
  http: ReturnType<typeof useDeckorateHttp>,
  args: GenerateArgs,
  abortState: AbortState,
): Promise<{ xml: string; infographicType: string | null }> {
  const result = await withRequestTimeout(abortState, (signal) =>
    postConvertInfographic(http, args.content, args.dims, {
      mode: args.mode,
      themeColors: args.themeColors,
      userDescription: args.userDescription,
      signal,
    }),
  );
  if (!result.valid || !result.xml) throw new Error(result.error || NO_INFOGRAPHIC_XML_MESSAGE);
  return { xml: result.xml, infographicType: result.infographicType };
}

/** Caches the first full-slide generation so every later fallback tier reuses
 *  it instead of re-asking the AI for the same thing. */
interface FullSlideCache {
  xml: string | null;
  infographicType: string | null;
}

interface RegionMergeOutcome {
  readonly merged: { base64: string; sourceSlideId: string | null } | null;
  readonly infographicType: string | null;
  readonly fullSlideXml: string | null;
}

/**
 * Tries the region-sized generation + merge first; on failure (either stage),
 * regenerates at full-slide size and retries the merge with that content
 * ("constrain" tier). Returns `merged: null` only once both tiers have
 * failed — the caller then falls through to a plain full-slide insert.
 */
async function resolveRegionMerge(
  http: ReturnType<typeof useDeckorateHttp>,
  content: InfographicContent,
  region: InfographicRegionTarget,
  slideDimsEmu: SlideDimensions,
  themeColors: ThemeColors | null,
  userDescription: string,
  abortState: AbortState,
): Promise<RegionMergeOutcome> {
  try {
    const { xml, infographicType } = await generateInfographicXml(
      http,
      {
        content,
        dims: { cx: region.geometry.cx, cy: region.geometry.cy },
        mode: "region",
        themeColors,
        userDescription,
      },
      abortState,
    );
    const merged = await mergeInfographicIntoRegion(xml, region.geometry, region.shapeNames);
    return { merged, infographicType, fullSlideXml: null };
  } catch (regionErr) {
    if (isInfographicAbortError(regionErr)) throw regionErr;
    logInfographicError("infographic-region-generate-failed", regionErr);
  }

  // Tracked outside the try so a merge failure (unlike a generation failure)
  // still returns the already-generated xml for the standalone fallback to reuse.
  let fullSlideXml: string | null = null;
  let infographicType: string | null = null;
  try {
    const generated = await generateInfographicXml(
      http,
      {
        content,
        dims: slideDimsEmu,
        mode: "full_slide",
        themeColors,
        userDescription,
      },
      abortState,
    );
    infographicType = generated.infographicType;
    fullSlideXml = generated.xml;
    const merged = await mergeInfographicIntoRegion(generated.xml, region.geometry, region.shapeNames);
    return { merged, infographicType, fullSlideXml };
  } catch (constrainErr) {
    if (isInfographicAbortError(constrainErr)) throw constrainErr;
    logInfographicError("infographic-region-constrain-failed", constrainErr);
    return { merged: null, infographicType, fullSlideXml };
  }
}

/** Returns the cached full-slide xml, generating (and caching) it on first use. */
async function ensureFullSlideXml(
  http: ReturnType<typeof useDeckorateHttp>,
  content: InfographicContent,
  slideDimsEmu: SlideDimensions,
  themeColors: ThemeColors | null,
  userDescription: string,
  cache: FullSlideCache,
  abortState: AbortState,
): Promise<string> {
  if (cache.xml) return cache.xml;
  const { xml, infographicType } = await generateInfographicXml(
    http,
    {
      content,
      dims: slideDimsEmu,
      mode: "full_slide",
      themeColors,
      userDescription,
    },
    abortState,
  );
  cache.xml = xml;
  cache.infographicType = infographicType;
  return xml;
}

/** Last-resort tier: rebuild every shape from scratch via PptxGenJS's native
 *  API, which always emits schema-valid OOXML (see infographicNativeBuilder.ts). */
async function nativeRebuildInsert(
  afterErr: unknown,
  fullSlideXml: string,
  slideDimsEmu: SlideDimensions,
  themeColors: ThemeColors | null,
): Promise<void> {
  logInfographicError("infographic-insert-retry-native", afterErr);
  const native = await buildNativeInfographicSlide(fullSlideXml, slideDimsEmu, themeColors);
  await insertInfographicResult(native.base64, null);
}

/** Shared, read-only context `insertWithFallback` needs for every fallback
 *  tier — bundled so the function itself only takes the values that change
 *  per call (the base64 payload, its source slide, and the replacement flag). */
interface InsertFallbackContext {
  readonly http: ReturnType<typeof useDeckorateHttp>;
  readonly content: InfographicContent;
  readonly slideDimsEmu: SlideDimensions;
  readonly themeColors: ThemeColors | null;
  readonly userDescription: string;
  readonly cache: FullSlideCache;
  readonly abortState: AbortState;
}

/**
 * Inserts `base64`, falling back to a standalone full-slide insert (abandoning
 * any merge) and finally to the native-rebuild tier when PowerPoint rejects
 * the base64 outright. Returns whether a merge survived the whole attempt.
 */
async function insertWithFallback(
  base64: string,
  sourceSlideId: string | null,
  usedReplacement: boolean,
  ctx: InsertFallbackContext,
): Promise<boolean> {
  try {
    await insertInfographicResult(base64, sourceSlideId);
    return usedReplacement;
  } catch (insertErr) {
    logInfographicError("infographic-insert-retry-standalone", insertErr);
    const fullSlideXml = await ensureFullSlideXml(
      ctx.http,
      ctx.content,
      ctx.slideDimsEmu,
      ctx.themeColors,
      ctx.userDescription,
      ctx.cache,
      ctx.abortState,
    );

    if (!usedReplacement) {
      // The initial attempt already WAS the standalone full-slide wrap (no
      // merge to abandon) — rebuilding an identical wrap would just repeat
      // the same rejection, so go straight to the native tier.
      await nativeRebuildInsert(insertErr, fullSlideXml, ctx.slideDimsEmu, ctx.themeColors);
      return false;
    }

    try {
      const standaloneBase64 = await buildInfographicSlideBase64(fullSlideXml, ctx.slideDimsEmu);
      await insertInfographicResult(standaloneBase64, null);
    } catch (standaloneErr) {
      await nativeRebuildInsert(standaloneErr, fullSlideXml, ctx.slideDimsEmu, ctx.themeColors);
    }
    return false;
  }
}

export function useConvertInfographic() {
  const http = useDeckorateHttp();
  const [state, setState] = useState<ConvertInfographicState>({ status: "idle" });
  // Guards a second run starting while one is in flight (fast double-click),
  // mirrors useFixAlignment/useBrandApply's isRunningRef convention.
  const isRunningRef = useRef(false);
  const isMountedRef = useRef(true);
  // Cancels whichever convert_infographic call is in flight — on timeout, on
  // Cancel, or on unmount — mirrors useFixAlignment's abortRef/timedOutRef.
  const abortRef = useRef<AbortController | null>(null);
  const timedOutRef = useRef(false);

  // Abandon any in-flight request when the panel closes — without this the
  // fetch outlives the component and its result is applied to the deck after
  // the user has navigated away.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  const safeSetState = useCallback((next: ConvertInfographicState) => {
    if (isMountedRef.current) setState(next);
  }, []);

  const run = useCallback(
    async (userDescription: string) => {
      if (isRunningRef.current) return;
      isRunningRef.current = true;
      timedOutRef.current = false;
      safeSetState({ status: "running", phase: "analyzing" });
      const abortState: AbortState = { abortRef, timedOutRef };

      try {
        const extraction = await extractInfographicContent();
        const [slideDimsEmu, themeColors] = await Promise.all([
          readSlideDimensionsEmu(),
          readDeckThemeColors(),
        ]);

        const cache: FullSlideCache = { xml: null, infographicType: null };
        const region: InfographicRegionTarget | null = extraction.mode === "region" ? extraction.region : null;

        let merged: { base64: string; sourceSlideId: string | null } | null = null;
        let usedReplacement = false;
        if (region) {
          const outcome = await resolveRegionMerge(
            http,
            extraction.content,
            region,
            slideDimsEmu,
            themeColors,
            userDescription,
            abortState,
          );
          merged = outcome.merged;
          cache.xml = outcome.fullSlideXml;
          cache.infographicType = outcome.infographicType;
          usedReplacement = merged !== null;
        }

        safeSetState({ status: "running", phase: "composing" });

        const base64 = merged
          ? merged.base64
          : await buildInfographicSlideBase64(
              await ensureFullSlideXml(
                http,
                extraction.content,
                slideDimsEmu,
                themeColors,
                userDescription,
                cache,
                abortState,
              ),
              slideDimsEmu,
            );
        const sourceSlideId = merged ? merged.sourceSlideId : null;

        usedReplacement = await insertWithFallback(base64, sourceSlideId, usedReplacement, {
          http,
          content: extraction.content,
          slideDimsEmu,
          themeColors,
          userDescription,
          cache,
          abortState,
        });

        safeSetState({ status: "done", infographicType: cache.infographicType, replacedSelection: usedReplacement });
      } catch (error) {
        const timedOut = timedOutRef.current;
        // A user-initiated cancel is an expected outcome, not a failure: it is
        // neither reported to telemetry nor shown as an error. A timeout IS a
        // failure (it points at a slow backend), so it takes the error path.
        if (isInfographicAbortError(error) && !timedOut) {
          safeSetState({ status: "cancelled" });
          return;
        }
        logInfographicError("infographic-run", error);
        const message = describeInfographicFailure(
          error,
          getInfographicErrorMessage(error, INFOGRAPHIC_FAILED_MESSAGE),
          { timedOut },
        );
        safeSetState({ status: "error", message });
      } finally {
        isRunningRef.current = false;
      }
    },
    [http, safeSetState],
  );

  /** Abandon an in-flight run. Nothing has been written to the deck yet unless
   *  a merge/insert step already completed, which cancel() cannot unwind. */
  const cancel = useCallback(() => {
    timedOutRef.current = false;
    abortRef.current?.abort();
  }, []);

  const reset = useCallback(() => safeSetState({ status: "idle" }), [safeSetState]);

  return { state, run, cancel, reset };
}
